/**
 * Downloads the caller's tag registry as a portable bundle from the debug
 * maintenance endpoint.
 *
 * Unlike the Postgres dump next door, the bundle is scoped to one organization
 * and carries no tenant column, so it can be imported into a different
 * environment without any post-import fixups.
 */

import { downloadMaintenanceBackup } from './download-maintenance-backup'

export type TagBundleReadings = 'none' | 'window'

export type TagBundleImportMode = 'merge' | 'replace'

export interface TagBundleUrlRewrite {
  from: string
  to: string
}

/** Leading line of a bundle: what it holds and which URLs it references. */
export interface TagBundleManifest {
  bundle_version: number
  exported_at: string
  /** Provenance only. The import writes into the caller's own organization. */
  source_organization_id?: string
  counts: Record<string, number>
  source_urls: string[]
  readings_window?: { from: string; to: string }
}

export interface TagBundleAppliedCounts {
  tags: number
  /** Present locally under an identity the write path cannot upsert through. */
  tags_skipped: number
  tags_failed: number
  webdev_endpoints: number
  opc_endpoints: number
  mtc_endpoints: number
  db_connections: number
  db_datapoints: number
  readings: number
}

export interface TagBundleAppliedRewrite {
  from: string
  to: string
  occurrences: number
}

/**
 * The graph's copy of the same URLs. Reported separately from the Postgres
 * counts because the graph write targets the active branch, which is a wider
 * blast radius than the org's tag tables.
 */
export interface TagBundleGraphResult {
  /** The branch whose graph was (or would be) rewritten. */
  branch: string
  /** Nodes rewritten per label. Labels that matched nothing are absent. */
  applied?: Record<string, number>
  /**
   * Why the graph was left alone. A skip never fails the import — the tag rows
   * have already landed — so this is surfaced as a warning rather than an error.
   */
  skipped_reason?: string
}

export interface TagBundleImportResult {
  dry_run: boolean
  mode: TagBundleImportMode
  /**
   * The organization the rows landed in, resolved from the caller's identity.
   * The request cannot name a target, so the response reports the one used.
   */
  target_organization_id?: string
  manifest: TagBundleManifest
  applied: TagBundleAppliedCounts
  rewrites?: TagBundleAppliedRewrite[]
  graph?: TagBundleGraphResult
  warnings?: string[]
}

export interface TagBundleImportRequest {
  file: File
  mode?: TagBundleImportMode
  dryRun?: boolean
  urlRewrites?: TagBundleUrlRewrite[]
  /**
   * Omit to take the server default — the graph follows the rewrite map — so a
   * rewritten registry and a stale graph do not disagree about where a gateway
   * lives.
   */
  applyToGraph?: boolean
}

export interface TagBundleExportOptions {
  /**
   * Readings are excluded by default: tag_readings is the highest-volume table
   * in the system, and a registry-only bundle is the common case.
   */
  readings?: TagBundleReadings
  /** RFC 3339 bounds for `readings: 'window'`. Defaults to the last 24 hours. */
  from?: string
  to?: string
}

export function tagBundleClientFilename(now = new Date()): string {
  const stamp = now
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z')
  return `tags-${stamp}.ndjson.gz`
}

export async function downloadTagBundle(
  fileName: string,
  options: TagBundleExportOptions = {}
): Promise<void> {
  const params = new URLSearchParams()
  if (options.readings) params.set('readings', options.readings)
  if (options.from) params.set('from', options.from)
  if (options.to) params.set('to', options.to)

  const query = params.toString()
  return downloadMaintenanceBackup('Tag bundle', `tag-bundle${query ? `?${query}` : ''}`, fileName)
}
